import { useCallback, useMemo, useRef, useState } from "react";
import { resolveTerms } from "@/shared/utils/resolveTerms";
import type { ResolvedTerm } from "@/shared/utils/resolveTerm";
import { parseCsvKeywords } from "../parsePastedList";
import {
  keepAsKeyword as keepTermAsKeyword,
  matchFromResolved,
  recordMatch,
  tagsToSave,
  withoutMatch,
  type TermMatch,
  type TermMatches,
} from "../termMatches";

/**
 * The tickers, companies and topics typed into a builder, with what the tag
 * lookup matched each one to.
 *
 * The builder's typing field, as a unit the combined builder can hold beside
 * its other parts. The rules — who is up for review, what "Keep as keyword"
 * does, which tags a list keeps — live in termMatches.ts.
 */
export function useTypedTerms() {
  const [tickers, setTickers] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [termMatches, setTermMatches] = useState<TermMatches>({});
  const [isResolving, setIsResolving] = useState(false);

  /**
   * The tickers as last rendered. addResolvedTerm runs in a loop after an
   * await, so it cannot read state, and recordMatch needs to know which
   * tickers were already there.
   */
  const listedTickers = useRef(tickers);
  listedTickers.current = tickers;

  const addResolvedTerm = useCallback((resolved: ResolvedTerm) => {
    if (resolved.ticker) {
      setTickers((prev) =>
        prev.includes(resolved.ticker!) ? prev : [...prev, resolved.ticker!]
      );
    } else if (resolved.keyword) {
      setKeywords((prev) =>
        prev.includes(resolved.keyword!) ? prev : [...prev, resolved.keyword!]
      );
    }
    const found = matchFromResolved(resolved);
    if (found) {
      const wasListed = listedTickers.current.includes(found.term);
      setTermMatches((prev) =>
        recordMatch(prev, found.term, found.match, wasListed)
      );
    }
  }, []);

  const removeTicker = useCallback((term: string) => {
    setTickers((prev) => prev.filter((t) => t !== term));
    setTermMatches((prev) => withoutMatch(prev, term));
  }, []);

  const removeKeyword = useCallback((term: string) => {
    setKeywords((prev) => prev.filter((k) => k !== term));
    setTermMatches((prev) => withoutMatch(prev, term));
  }, []);

  const keepAsKeyword = useCallback(
    (term: string) => {
      const next = keepTermAsKeyword(
        { tickers, keywords, matches: termMatches },
        term
      );
      setTickers(next.tickers);
      setKeywords(next.keywords);
      setTermMatches(next.matches);
    },
    [tickers, keywords, termMatches]
  );

  /**
   * Replace everything with a saved list's terms. They were matched when they
   * were added, but pref-storage keeps no record of it, so they come back as
   * the plain terms they are stored as.
   */
  const load = useCallback((terms: { tickers?: string[]; keywords?: string[] }) => {
    setTickers(terms.tickers ?? []);
    setKeywords(terms.keywords ?? []);
    setTermMatches({});
  }, []);

  const clear = useCallback(() => load({}), [load]);

  /**
   * A ticker CSV: every row filed as a ticker, whatever the lookup says —
   * someone uploading a book of symbols has said what they are. The lookup
   * still runs, for the tag and to turn "Apple" into AAPL, and a row it
   * replaced is up for review like a typed one.
   */
  const addTickerCsv = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      // parseCsvKeywords, not parseCsvTickers: the ticker parser upper-cases,
      // turning "Apple" into a ticker called APPLE before the lookup sees it.
      const rows = parseCsvKeywords(String(event.target?.result ?? ""));
      if (rows.length === 0) return;
      setIsResolving(true);
      try {
        const { resolved } = await resolveTerms(rows, { nameTickers: true }).catch(
          () => ({ resolved: [] as ResolvedTerm[] })
        );
        const byInput = new Map(
          resolved.map((r) => [r.input.trim().toUpperCase(), r])
        );
        const symbols: string[] = [];
        const matches: [string, TermMatch][] = [];
        for (const row of rows) {
          const hit = byInput.get(row.trim().toUpperCase());
          const symbol = (hit?.ticker ?? row).trim().toUpperCase();
          if (!symbol) continue;
          symbols.push(symbol);
          const match = hit ? matchFromResolved(hit)?.match : undefined;
          if (match) matches.push([symbol, match]);
        }
        const listed = listedTickers.current;
        setTickers((prev) => [...prev, ...symbols.filter((s) => !prev.includes(s))]);
        if (matches.length > 0) {
          setTermMatches((prev) =>
            matches.reduce(
              (next, [symbol, match]) =>
                recordMatch(next, symbol, match, listed.includes(symbol)),
              prev
            )
          );
        }
      } finally {
        setIsResolving(false);
      }
    };
    reader.readAsText(file);
  }, []);

  /** Tags the list keeps: one per matched term still in it. */
  const keptTags = useMemo(
    () => tagsToSave(tickers, keywords, termMatches),
    [tickers, keywords, termMatches]
  );

  return {
    tickers,
    keywords,
    termMatches,
    keptTags,
    hasTerms: tickers.length > 0 || keywords.length > 0,
    isResolving,
    addResolvedTerm,
    removeTicker,
    removeKeyword,
    keepAsKeyword,
    addTickerCsv,
    load,
    clear,
  };
}

export type TypedTerms = ReturnType<typeof useTypedTerms>;
